import { ProxyTypeName } from '@fd/common/biz/proxyType';
import { honoPrivateClient } from '@/api/honoClient';
import { useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import useSWR from 'swr';

const $get = honoPrivateClient.traffic.usage.$get;

export type TimeUnit = 'hour' | 'day' | 'month';
export type TimeFrame = {
  startTime: Date;
  endTime: Date;
};

const fetcher = async (
  startTime: Date,
  endTime: Date,
  proxyType?: ProxyTypeName,
  poolId?: number
) => {
  const res = await $get({
    query: {
      endTime: endTime.toISOString(),
      startTime: startTime.toISOString(),
      proxyType,
      poolId: poolId?.toString(),
    },
  });
  if (!res.ok) {
    throw new Error('Failed to fetch data');
  }
  return await res.json();
};

type TrafficUsageResponse = Awaited<ReturnType<typeof fetcher>>;

function aggregateData(
  data: TrafficUsageResponse,
  timeUnit: TimeUnit,
  startTime: Date,
  endTime: Date
) {
  // Create array of time slots from start to end
  const timeSlots: { usage_hour: string; label: string; gb_used: number }[] =
    [];
  let currentTime = new Date(startTime);
  currentTime.setHours(0, 0, 0, 0);

  while (currentTime <= endTime) {
    const formattedDate = currentTime.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    let label = formattedDate;
    if (timeUnit === 'day') {
      label = formattedDate.slice(0, 10); // DD/MM/YYYY
    } else if (timeUnit === 'month') {
      label = formattedDate.slice(3, 10); // MM/YYYY
    }

    timeSlots.push({
      usage_hour: currentTime.toISOString(),
      label,
      gb_used: 0,
    });

    // Increment by time unit
    if (timeUnit === 'hour') {
      currentTime = new Date(currentTime.setHours(currentTime.getHours() + 1));
    } else if (timeUnit === 'day') {
      currentTime = new Date(currentTime.setDate(currentTime.getDate() + 1));
    } else {
      currentTime = new Date(currentTime.setMonth(currentTime.getMonth() + 1));
    }
  }

  // Add actual data
  data.forEach((item: TrafficUsageResponse[number]) => {
    const itemDate = new Date(item.usage_hour);
    const matchingSlot = timeSlots.find((slot) => {
      const slotDate = new Date(slot.usage_hour);
      if (timeUnit === 'hour') {
        return slotDate.getTime() === itemDate.getTime();
      } else if (timeUnit === 'day') {
        return slotDate.toDateString() === itemDate.toDateString();
      } else {
        return (
          slotDate.getMonth() === itemDate.getMonth() &&
          slotDate.getFullYear() === itemDate.getFullYear()
        );
      }
    });

    if (matchingSlot) {
      matchingSlot.gb_used += item.gb_used;
    }
  });

  return timeSlots;
}

type UseTrafficUsageOptions = {
  dateRange?: DateRange;
  timeUnit: TimeUnit;
  proxyType?: ProxyTypeName | 'all';
  poolId?: number;
};

export default function useTrafficUsage(options: UseTrafficUsageOptions) {
  const { timeUnit, dateRange, proxyType, poolId } = options;
  const now = new Date();
  let endTime = now;
  let startTime = useMemo(() => {
    const now = new Date();
    return new Date(now.setMonth(now.getMonth() - 1));
  }, []);

  if (dateRange?.from) {
    // Single day selected or same start/end date
    const isSingleDaySelection =
      !dateRange.to || dateRange.from.getTime() === dateRange.to.getTime();

    if (isSingleDaySelection) {
      // Set time range to full day (00:00:00 - 23:59:59)
      const selectedDate = new Date(dateRange.from);
      startTime = new Date(selectedDate.setHours(0, 0, 0, 0));
      endTime = new Date(selectedDate.setHours(23, 59, 59, 999));
    } else {
      // Date range selected - use exact dates
      startTime = dateRange.from;
      endTime = dateRange.to!;
    }
  }

  const response = useSWR(
    ['/traffic/usage', startTime, endTime, proxyType, poolId],
    () =>
      fetcher(startTime, endTime, proxyType === 'all' ? undefined : proxyType, poolId)
  );

  const aggregatedData = useMemo(() => {
    return aggregateData(response.data ?? [], timeUnit, startTime, endTime);
  }, [response.data, timeUnit, startTime, endTime]);

  return { ...response, data: aggregatedData };
}
